import { NumberDisplayFormat } from '@teko/types/ecommerce';
import { SvgIconProps, Theme as UITheme } from '@teko/ui';
import { FlagState } from 'libs';
import { LocationOption } from 'server/internal-services/public-bff.interface';
import type { BrandingConfig, ChatLabels } from 'teko-assistant-ui';
import { JSONObject } from '../types';

interface Logo {
  path: string;
  height?: number;
  width?: number;
}

interface TenantLogo {
  full: Logo;
  lite: Logo;
  mobile: Logo;
  loading: Logo;
}

interface TerminalLocation {
  ward: string;
  district: string;
  province: string;
}

export interface Terminal {
  id: number;
  code: string;
  label: string;
  provinceCodes?: string[];
  locationCode?: string;
  isDefault?: boolean;
  location?: TerminalLocation;
}

interface Location {
  provinces?: string[];
  districts?: string[];
  wards?: string[];
}

interface MobileAppInfo {
  appLink: string;
  appName: string;
}

export interface SchemaData {
  [key: string]: string;
}

export interface SiteMeta {
  title: string;
  description: string;
  image: string;
  dcmaVerificationContent?: string;
  fbDomainVerificationContent?: string;
}

interface TenantCustomInfo {
  icon: SvgIconProps;
  title: string;
  href?: string;
  regionName?: string;
  hasArrow?: boolean;
}

export enum LocationMessageType {
  'None' = '0',
  'Hint' = '1',
  'Error' = '2',
}

export interface TenantConfig {
  terminal: Terminal[] | string;
  terminalId: number;

  // Login via federated_broker proof on the URL, not SDK.
  enableFederationBroker?: boolean;

  /* @deprecated */
  channel?: string;
  /* @deprecated */
  channelId?: number;
  /* @deprecated */
  channelType?: string;
  /* @deprecated */
  channelCode_in?: string;

  supportedPhone?: string;
  logo: TenantLogo;
  isDisplayMessenger?: boolean;
  contactLink?: string;
  zaloContactLink?: string;
  notificationAppId: string;
  allowCommentAndReceiveReply?: boolean;
  topBannerZone?: string;
  locationsLimit?: Location;
  theme: Partial<UITheme>;
  scopes?: string[];
  sellerId?: number; // phongvu
  clientConsultantPhone?: string; //phongvu
  businessCustomerPhone?: string; //phongvu
  showrooms?: boolean; //phongvu
  schemaWebsite?: SchemaData;
  mobileAppInfo?: MobileAppInfo;
  disableATCFromListingPageRegexes?: string[];

  // site meta data like logo, title ...
  meta: SiteMeta;
  addSchemaItemList?: boolean;
  addSchemaBreadcrumbList?: boolean;

  loyaltyExternal?: boolean;

  // customer can create return request
  returnRequestFeature?: boolean;

  // require user to identity infomation (dob, sex, name) before interacting with website
  identityRequired?: 'dealer-required' | 'dealer-not-required';

  // require user to login before interact with website
  // true: umbala, karavan
  // false: others
  requireLogin?: boolean;

  platformId?: number;

  customInfo?: TenantCustomInfo[]; // custom info which is displayed on Account page for tenant

  // Payment type: 'normal' | 'bank' | 'terra' | 'd365'
  // normal -> get payment method list from ps
  // bank -> ibanking
  // terra -> vnpaymerchant (karavan)
  // d365 -> do not payment, only create order (meatdeli)
  paymentType: string;
  hasNews?: boolean;
  mobileBuildPc?: boolean;
  notEditCustomerType?: boolean; // flag check can edit customer type with dealer cp
  availableLanguages?: string[];
  // Threshold that product is free shipped
  // default is 300000 (800000 for phongvu desktop) (this can be changed)
  // negative for disable this feature
  freeShippingThreshold?: number;
  hideAccountInfo?: boolean;

  //@TODO: replace by enum
  template?: string;

  needConfirmMembershipAgreement?: boolean;

  // value to hidden delivery information section on showroom checkout screen
  hideCheckoutdeliveryInfomation?: boolean;

  // config boolean check override footer
  noOverride?: boolean;

  // config for footer data
  footerDataConfig?: JSONObject;

  // config for vietin footer data
  vietinFooterDataConfig?: JSONObject;

  // config for top bar button
  rightMenuTopBarButtonConfigs?: JSONObject[];
  leftMenuTopBarButtonConfigs?: JSONObject[];

  // ECOM-296 configure the number of times the user visits the site without answering the questionnaries
  askAgainQuestionnairesConfig?: number;

  // ECOM-297 iamClientId of new website is obtained from Get websites list API
  iamClientId?: string;

  // ECOM-297 paymentSecretKey of new website is obtained from Get websites list API
  paymentSecretKey?: string;

  // ECOM-297 paymentTerminalCode of new website is obtained from Get websites list API
  paymentTerminalCode?: string;

  // ECOM-297 iamOauthDomain of new website is obtained from Get websites list API
  iamOauthDomain?: string;

  // ECOM-359 websiteId to get area for new website
  websiteId?: number;

  // UX4BUYER-5435: show uom in product card
  isDisplayUom?: boolean;
  // ECOM-368 support decimal quantity
  uomCodeToDisplayDecimalQuantity?: string;

  requiresUserOverEighteenYearsOld?: boolean;

  withoutPayment?: boolean;

  logoUrl?: string;

  // is email required in delivery information
  deliveryRequireEmail?: boolean;

  // ECOM-411: do not show popup for bot, which caused large area paint
  isBot?: boolean;

  serviceWorkerScripts?: Array<string>;

  // tracking app Id
  trackingId?: string;

  // flag for PB-1887 and replace for allowRenderCustomPage (ECOM-424)
  epicPb1887?: boolean;

  // flag for epic omni 976
  epicOmni976?: boolean;

  smartActivationConfig?: {
    appId?: string;
    sessionPeriodMinutes?: number;
  };

  // ECOM-284: to map terminalId from desktop terminalId
  // Website management only store desktop terminalId
  mobileTerminalIdMapping?: {
    [key: string]: { terminalId: number; terminalCode: string };
  };

  location?: string;

  locations?: LocationOption[];

  epicOmni964?: boolean;

  // flag for epic omni 1058
  epicOmni1058?: boolean;
  // flag for epic omni 1212
  epicOmni1212?: boolean;
  // flag for epic omni 1286
  epicOmni1286?: boolean;

  // When ON: clicking "Add to cart" on a product card that has a combo
  // (bestComboDiscount != 0) opens the combo-selection popup instead of
  // adding the product straight to cart. Depends on epicOmni1286
  // for combo data & the bestComboDiscount signal.
  enableComboPopupOnATC?: boolean;

  // flag for epic omni 1825
  epicOmni1825?: boolean;
  epicOmni1825SuggestAddress?: boolean;

  epicOmni1584?: boolean;

  // flag for ecic omni-1589
  epicOmni1589?: boolean;

  // flag check address when lack ward
  isValidateAddressInCheckout?: boolean;
  isAllowGetTerminalFromTerra?: boolean;
  // Show new product gifts ui or not
  showNewProductGift?: boolean;

  // only use in UX4BUYER-5123.
  // Todo: alter messageLink by contactLink when pushdy off in soibien.vn
  messengerLink?: string;

  disableEpicEcom284?: boolean;

  // keywords are displayed below the search box
  popularKeywords?: [{ keyword: string; link: string }];

  // flag to show collection page title or not?
  showCollectionPageTitle?: boolean;
  // create a separate flag instead of relying con ecom284
  useTerminalId?: boolean;
  tempiWebsiteId?: number;
  // config default sort option for listing page
  defaultSortOptionCode?: string;
  // config enable A/B testing for discovery search algorithm
  enableSearchAlgoABTesting?: boolean;
  lang?: {
    [langCode: string]: {
      [key: string]: string;
    };
  };
  floatingIcon?: {
    imageUrl: string;
    href: string;
    startDate: string;
    endDate: string;
  };

  // use it instead of baseDomain for affiliate sites, which will fix the duplicate content issue
  canonicalDomain?: string;
  profileRequiredAfterLogin?: boolean;
  // ECOM-483, privacy policies time to live by day
  privacyPolicyCacheTTL?: number;
  // ECOM 481, buy now
  allowBuyNow?: boolean;
  // UX4BUYER-6250
  fixedVariants?: {
    [sku: string]: {
      [key: string]: Array<{
        label: string;
        url: string;
        sku: string;
      }>;
    };
  };
  epicEcom480?: boolean;
  tempiWebsiteDomain?: string;
  // UX4BUYER-6290
  termsAndPoliciesHref?: string;

  organizationSchema?: string;

  // UX4BUYER-6393: POC --> TODO: deprecate after live successfully subsections from discovery
  subSections?: {
    [key: string]: SubSectionBlock;
  };
  // epic omni-1034
  epicOmni1034?: boolean;
  epicOmni1540?: boolean;
  epicOmni1539?: boolean;
  // limit time query PENDING_PAYMENT order in homepage
  pendingPaymentCreatedAtGteInHour?: number;
  // feature https://jira.teko.vn/browse/UX4BUYER-6807
  displayHotlineFloatIcon?: boolean;
  // custom image for hotline float icon
  hotlineFloatIconCustomImage?: string;

  isDmsFlow?: boolean;
  // ecom-489
  numberDisplayFormat?: NumberDisplayFormat;
  // omni-1403
  epicOmni1403?: boolean;
  // UX4BUYER-6793
  displayPrivacyPoliciesInProfileForm?: boolean;
  // flag for epic OMNI-1459
  epicOmni1459?: boolean;
  // merchant installment method code to call API getInstallmentTerms
  merchantInstallmentMethodCode?: string;
  // omni-1428
  epicOmni1428?: boolean;
  // flag for epic omni-948
  epicOmni948?: boolean;
  //flag for epic omni-1538
  epicOmni1538?: boolean;
  // flag for button unlink member ship
  epicOmni1538UnlinkMembership?: boolean;
  // flag for epic omni-1561
  epicOmni1561?: boolean;
  // flag for epic omni-1576
  epicOmni1576?: boolean;
  // Turn on = disable change payment method button in confirm payment screen (related to epic omni-1580)
  disableChangePaymentMethods?: boolean;
  // flag for epic omni-1585
  epicOmni1585?: boolean;
  // flag for epic omni-1595
  epicOmni1595?: boolean;
  // flag for epic omni-1550
  epicOmni1550?: boolean;
  // flag to hide write review button
  epicOmni1550HideReviewButton?: boolean;
  // flag for epic OMNI-1582
  epicOmni1582?: boolean;
  // flag for epic OMNI-1604
  // should also enable flag OMNI-1538, since 1604 extends logic of this epic
  epicOmni1604?: boolean;
  // flag for epic OMNI-1689
  epicOmni1689?: boolean;
  // flag for epic OMNI-1821
  epicOmni1821?: boolean;
  // flag allow show pages configured in website-editor in shop-front
  // without switching homepage to version configured in website-editor
  showPbPagesFromWebsiteEditor?: boolean;
  buildPcMapping?: Record<
    string,
    {
      type: string;
      name: string;
      slug: string;
      categories: string[];
      icon: string;
    }
  >;

  bomMapping?: Record<
    string,
    {
      type: string;
      name: string;
      icon: string;
      categories: string[];
      slug: string;
    }
  >;

  sortByCreatedAt?: boolean;
  // UX4BUYER-6993 - for SGC
  forceUpdatePhoneNumber?: boolean;
  // OMNI-1540: show profile update form after register
  isNeedToUpdateProfile?: boolean;
  epicOmni1553?: boolean;
  // product collection seller info
  schemaProductCollectionSellerInfo?: unknown;
  // turn on omni-1058 for repayment page
  epicOmni1058Repayment?: boolean;
  // enable to render floating icons based on defined floatingIconsDisplayPriority
  renderFloatingIconsDynamically?: boolean;
  // floating icons display priority
  // first icon in array will be displayed first, ...
  floatingIconsDisplayPriority?: Array<
    typeof ICON_DISPLAY_PRIORITY[keyof typeof ICON_DISPLAY_PRIORITY]
  >;
  // path to mobile categories page, config this to redirect user to categories page instead of show popup categories
  mobileCategoriesPagePath?: string;
  // omni-1538 customer type require tax code
  customerTypeRequireTaxCode?: number[];
  // omni-1538 sgc occupations
  sgcOccupations?: Array<{
    value: string;
    text: string;
  }>;
  // UX4BUYER-7198: show Zalo share button
  zaloOAId?: string;
  zaloUrl?: string;
  otpRequestCountDown?: number;
  //ecom-495
  epicEcom495?: boolean;
  // OMNI-1547
  orderPromotionSource?: Array<{ text: string; value: string; imgUrl: string }>;
  // Due to the system still refunds coupon in some cases and SGC's business also refunds vouchers in some specific cases (thread: https://tekoers.slack.com/archives/C01G1TY4YUB/p1747016485076719)
  isNotShowWarningApplyCoupon?: boolean;
  // omni-1359 also use mobile membership card UI but has some differences with omni-1538, need a flag for display logic
  showMobileMembershipCard?: boolean;
  // UX4BUYER-8568: Apply for CP, show popup when checkout
  showRemindSpendPointPopup?: boolean;
  // UX4BUYER-7536 - config Pick-up point tab name according to platform
  pickupPointTabName?: string; // need to add key translate to file sites\shop-front\lang\en.json
  pendingDeliveryHours?: number;
  // UX4BUYER-7516: The feature depends on the app's function, so a flag needs to be set to avoid blocking the release
  allowToScanMembershipBarCode?: boolean;
  allowNewTaxCodePattern?: boolean;
  enableWarningMessage?: boolean;
  // Show text "Mô tả sản phẩm" on product detail screen according to platform
  detailDescriptionLabel?: string; // need to add key translate to file sites\shop-front\lang\en.json
  // Show text "Thông tin chi tiết" (Desktop) & "Chi tiết sản phẩm" (Mobile) on product detail screen according to platform
  productInformationLabel?: string; // need to add key translate to file sites\shop-front\lang\en.json
  // Show text "Thông số kĩ thuật" on product detail screen according to platform
  productSpecificationLabel?: string; // need to add key translate to file sites\shop-front\lang\en.json
  vatAddressWarningText?: string;
  // Flag to check if need to check loyaltyMemberId from profile before get loyalty member info
  needToCheckLoyaltyMemberId?: boolean;
  // Flag to check if need to fetch loyalty member info globally
  notFetchLoyaltyMemberInfoGlobally?: boolean;
  appName?: string;
  // Flag to check if need to show warning when user does not have loyalty member id in Cart screen
  needToShowWarningNoLoyaltyMemberInCart?: boolean;
  instructionLinkForLoyaltyAccount?: string;
  // AI Agent code for chatbot epic OMNI-1591
  chatbotAgentCode?: string;
  // https://jira.teko.vn/browse/UX4BUYER-7769
  pushTrackingForAdvanceTransform?: boolean;
  originalTerminalId?: number;
  // flag to turn on new layout for SGC
  displayNewLayout?: boolean;
  displayNewLayoutForProductDetailSGC?: boolean;
  // flag to turn on the new SGC layout for all listing pages
  // (/c, /search, brands, sellers, cua-hang)
  displayNewLayoutForListingSGC?: boolean;
  // flag to turn on the new SGC product card (UX4BUYER-9303)
  displayNewProductCardSGC?: boolean;
  // https://jira.teko.vn/browse/UX4BUYER-7867
  displayPublicCouponInRelatedPromotions?: boolean;
  // epicOmni1576: url to shipping policy detail page
  detailShippingPolicyUrl?: string;
  // OMNI-1636: url to pre-order deposit policy detail page
  preOrderDepositPolicyUrl?: string;
  // flag to enable/disable showing return to previous page arrow in detail page
  showDetailPageGoBackArrow?: boolean;
  // payment promotions
  paymentPromotions?: {
    logoUrl: string;
    altLogo?: string;
    content: string;
    expirationDate?: string;
  }[];
  // https://jira.teko.vn/browse/UX4BUYER-7774
  displayFilterTypeCategory?: boolean;
  // flag to turn on new layout page builder v1
  displayNewLayoutForPageBuilderV1?: boolean;
  // https://jira.teko.vn/browse/UX4BUYER-8274
  isAllowATCFromListing?: boolean;
  // https://jira.teko.vn/browse/UX4BUYER-8318
  isIdleCallbackTrackingAddToCart?: boolean;
  // https://jira.teko.vn/browse/OMNI-1643
  epicOmni1643?: boolean;
  // Flag for turn on payment promotions, separate from UI flag: https://jira.teko.vn/browse/UX4BUYER-9317
  isHighlightedOnPromotionBlock?: boolean;
  // https://jira.teko.vn/browse/UX4BUYER-8719
  hideProviderInfo?: boolean;
  // enable/disable show warning in checkout screen
  // warning customer to verify new address after merger
  // should only be enabled with flag epicOmni1589
  warningVerifyAddressAfterMerger?: boolean;
  // https://jira.teko.vn/browse/UX4BUYER-8820
  chatbotConfig?: {
    appId: string;
    bffUrl: string;
    transportMode?: 'rest' | 'websocket' | 'sse';
    primaryColor?: string;
    sidebarWidth?: number;
    branding?: BrandingConfig;
    labels?: Partial<ChatLabels>;
    quickActions?: {
      key: string;
      label: string;
      href?: string;
    }[];
    // https://jira.teko.vn/browse/PB-7306
    // Whitelist for the chat Agent. Patterns support wildcard '*' (exact when absent).
    // When omitted or both lists empty, the Agent is hidden for everyone.
    accessControl?: {
      // Matched against user.email, e.g. "*@teko.vn", "qa+1@gmail.com". Case-insensitive.
      emailPatterns?: string[];
      // Exact-match list of user.telephone, e.g. "0987654321".
      phones?: string[];
    };
  };
  // https://jira.teko.vn/browse/UX4BUYER-8759
  epicOmni1643Sgc?: boolean;
  // https://jira.teko.vn/browse/UX4BUYER-8931
  displayPaymentMethodsAmount?: number;
  // epic https://jira.teko.vn/browse/OMNI-1551
  epicOmni1551?: boolean;
  // epic https://jira.teko.vn/browse/OMNI-1790
  epicOmni1790?: boolean;
  // epic cookie consent https://jira.teko.vn/browse/OMNI-1936
  epicOmni1936?: boolean;
  // when enabled, disable the epicOmni1936 cookie-consent behavior when running inside webview
  // should only be enabled if epicOmni1936 is also enabled
  disableEpicOmni1936InWebview?: boolean;
  // epic keyword redirection https://jira.teko.vn/browse/ECOM-482
  epicEcom482?: boolean;
  // flag to turn on the new checkout layout (checkout revamp),
  // migrated cluster by cluster; off => keep the legacy checkout screen
  displayNewCheckoutLayout?: boolean;
}

export const ICON_DISPLAY_PRIORITY = {
  PHONE: 'phone',
  ZALO: 'zalo',
  MESSENGER: 'messenger',
  SCROLL_TOP: 'scroll-top',
  CHATBOT: 'chatbot',
  CHATBOT_AI: 'chatbot-ai',
  // Externally-injected Zalo OA chat widget (.zalo-chat-widget, via GTM/tempi). It renders
  // outside React as a fixed node; FloatingIconList adopts it as a slot by pinning it to an
  // in-stack anchor so it lines up with the other floating icons.
  ZALO_OA: 'zalo-oa',
} as const;

// this class is for backward compatible
// with existing code
// there is no particular reason why certain attributes
// reside in TenantEnv not TenantConfig
// historically TenantEnv is an interface that is used
// in many places.
export interface TenantEnv extends TenantConfig {
  terminal: string;
  tenant: string;
  originalTerminal?: Terminal[];
  getProcessEnv: (variable: string) => string;
  isMobile: boolean;
  partner?: string;
  baseDomain?: string;
  location?: string;
  megaMenuType: 'horizontal' | 'vertical';
  alignMegaMenu: 'left' | 'right' | 'space-between';
  hiddenBottomBar?: boolean;
  currentLanguage?: string;

  // temporary attribute to allow no index page like sofm.phongvu.vn
  noIndex?: boolean;

  needConfirmPolicyAndSalePrivacy?: boolean;
  featureFlags?: FlagState;

  keywordToCollectionMapping?: Iterable<readonly [string, string]>;

  // Feature show confirm modal after login. Ref: https://jira.teko.vn/browse/UX4BUYER-6052
  showConfirmModalAfterLogin?: boolean;

  clearCacheAfterSwitchLocation?: boolean;

  // Feature show popup after user scroll page. From ticket https://jira.teko.vn/browse/UX4BUYER-6489
  showPopupAfterScrollPage?: boolean;

  // Feature https://jira.teko.vn/browse/UX4BUYER-6695
  isDisableLuckyDrawPhoneInput?: boolean;

  // flag for the task https://jira.teko.vn/browse/UX4BUYER-7388
  applyNewLogicToCalculateComboPrice?: boolean;

  // flag for the task https://jira.teko.vn/browse/UX4BUYER-8791 to show gift value in related promotions in product detail page
  showGiftValueInRelatedPromotions?: boolean;

  tekReviewAppId?: number;

  epicOmni1632?: boolean;
}

export interface Area {
  id: string;
  content: string;
}

export interface SalesAndPrivacyPolicyHref {
  salesPolicy: string;
  privacyPolicy: string;
}

export interface ISubSectionBrand {
  logo?: string;
  url?: string;
}

export interface ISubSectionOtherItem {
  description: string;
  image?: string;
  url: string;
}

export interface ISubSectionOther {
  label: string;
  items: ISubSectionOtherItem[];
}

export interface SubSectionBlock {
  brands?: ISubSectionBrand[];
  others?: ISubSectionOther[];
}
